import * as React from 'react';
import { Form } from 'react-bootstrap';

/** Cell values reach the editor as text; anything empty edits as a blank field. */
const asText = (value: string | number | boolean | null | undefined): string =>
  (value === null || value === undefined ? '' : String(value));

interface Props {
  value: string | number | boolean | null | undefined;
  editable?: 'text' | 'checkbox' | 'select';
  options?: { label: string; value: string }[];
  rendered: React.ReactNode;
  onCommit: (value: string | boolean) => void;
}

/**
 *  A grid cell that turns into an input on double click, the interaction
 *  jqxGrid provided with editmode: 'dblclick'. Checkboxes commit immediately
 *  since there is nothing to type.
 */
export default function EditableCell(props: Props) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState('');

  if (props.editable === 'checkbox') {
    return (
      <Form.Check
        checked={!!props.value}
        onChange={(e) => props.onCommit(e.target.checked)}
      />
    );
  }

  if (!props.editable) {
    return <>{props.rendered}</>;
  }

  if (!editing) {
    return (
      <div
        onDoubleClick={() => {
          setDraft(asText(props.value));
          setEditing(true);
        }}
      >
        {props.rendered}
      </div>
    );
  }

  const commit = () => {
    setEditing(false);
    if (draft !== asText(props.value)) {
      props.onCommit(draft);
    }
  };

  if (props.editable === 'select') {
    return (
      <Form.Control
        as="select"
        size="sm"
        autoFocus
        value={draft}
        onChange={(e) => {
          setDraft(e.target.value);
          setEditing(false);
          props.onCommit(e.target.value);
        }}
        onBlur={() => setEditing(false)}
      >
        {(props.options || []).map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </Form.Control>
    );
  }

  return (
    <Form.Control
      size="sm"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          commit();
        }
        if (e.key === 'Escape') {
          setEditing(false);
        }
      }}
    />
  );
}
